import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mic } from "lucide-react";
import { GoogleReviewsBox } from "./GoogleReviewsBox";
import { VoiceCard, VOICE_T, voiceFlag } from "./VoiceReviewsBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChevronMini = ({ open }) => (
  <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// Box UNICA: Voci della Casa + Recensioni Google, disclaimer in fondo SOLO se ci sono voci.
export default function HouseVoicesReviews({ propertyId, accent = "#d04260", lang = "it", fontFamily, google = {} }) {
  const t = VOICE_T[lang] || VOICE_T.it;
  const [voices, setVoices] = useState(null);
  const [discOpen, setDiscOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/voice-reviews/property/${propertyId}`).then(r => setVoices(r.data)).catch(() => setVoices(null));
  }, [propertyId]);

  const hasVoices = !!(voices && voices.count > 0);
  const hasReviews = !!(google.reviews && google.reviews.length > 0);
  if (!hasVoices && !hasReviews) return null;

  const firstSentence = t.disclaimer.split(". ")[0] + ".";

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
      className="mt-5 md:mt-6 rounded-3xl border border-zinc-200/80 bg-white/90 backdrop-blur-sm overflow-hidden"
      style={fontFamily ? { fontFamily } : undefined}
      data-testid="house-voices-reviews"
    >
      <style>{`@keyframes voicePulse{0%,100%{transform:scaleY(0.5)}50%{transform:scaleY(1)}}`}</style>

      {hasVoices && (
        <div data-testid="voice-reviews-box">
          <div className="p-5 md:p-7 pb-3">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shrink-0" style={{ background: accent }}><Mic className="w-5 h-5" /></span>
              <div className="min-w-0">
                <h3 className="font-black text-lg text-zinc-900 leading-tight">{t.title}</h3>
                <p className="text-xs text-zinc-500">{t.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-5 mt-4">
              <div><div className="text-2xl font-black" style={{ color: accent }}>{voices.avg_rating}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{t.avg}</div></div>
              <div><div className="text-2xl font-black text-zinc-900">{voices.count}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{t.count}</div></div>
              {voices.top_countries?.length > 0 && (
                <div className="flex items-center gap-1 text-2xl ml-auto">{voices.top_countries.map(c => <span key={c}>{voiceFlag(c)}</span>)}</div>
              )}
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto px-5 md:px-7 pb-5 snap-x scrollbar-hide" data-testid="voice-carousel">
            {voices.items.map(item => <VoiceCard key={item.id} item={item} accent={accent} lang={lang} />)}
          </div>
        </div>
      )}

      {/* Divisore tra voci e recensioni */}
      {hasVoices && hasReviews && <div className="mx-5 md:mx-7 border-t border-black/5" />}

      {/* Recensioni Google (embedded, senza card propria) */}
      {hasReviews && <GoogleReviewsBox embedded accent={accent} {...google} />}

      {/* Disclaimer in fondo alla box UNICA — solo se ci sono voci */}
      {hasVoices && (
        <div className="px-5 md:px-7 pb-5 pt-1 border-t border-black/5">
          <p className="text-[10px] leading-relaxed text-zinc-400 mt-3">{discOpen ? t.disclaimer : firstSentence}</p>
          <button onClick={() => setDiscOpen(o => !o)} className="mt-1.5 text-[10px] font-bold inline-flex items-center gap-1" style={{ color: accent }} data-testid="voice-disclaimer-toggle">
            {discOpen ? t.readLess : t.readMore}
            <ChevronMini open={discOpen} />
          </button>
        </div>
      )}
    </motion.section>
  );
}
